/**
 * Pricing Utility
 * 
 * Calculates pricing based on current user count with tiered pricing model.
 * Supports test mode bypass for development/testing.
 */

/**
 * Pricing tiers based on user count
 * Format: [minUsers, maxUsers, priceInEuros]
 * Updated for Early Bird strategy:
 * - Free for first 250 users
 * - Yearly subscription after 250, increasing in price in stages up to 10,000 users
 */
const PRICING_TIERS: Array<[number, number, number]> = [
  [0, 250, 0],          // Free for first 250 users (Early Bird)
  [250, 1000, 2.50],    // €2.50/year for users 250-1000
  [1000, 2500, 5.00],   // €5.00/year for users 1000-2500
  [2500, 5000, 7.50],   // €7.50/year for users 2500-5000
  [5000, 10000, 10.00], // €10.00/year for users 5000-10000
  [10000, Infinity, 15.00], // €15.00/year for users 10000+
]

/**
 * Check if the user qualifies for the free Early Bird lifetime tier
 * @param userNumber The sequence number of the user
 */
export function isEarlyBird(userNumber: number): boolean {
  return userNumber <= 250
}

/**
 * Check if the current phase is the Yearly Subscription phase
 * @param userCount Total number of users
 */
export function isYearlyPhase(userCount: number): boolean {
  return userCount >= 1000
}

/**
 * Check if web payments (Stripe) are currently enabled
 * @returns false until KVK registration is complete
 */
export function isWebPaymentEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ENABLE_WEB_PAYMENTS === 'true'
}

/**
 * Calculate price based on user count
 * @param userCount Current number of active users
 * @returns Price in euros (0 if free tier)
 */
export function calculatePrice(userCount: number): number {
  for (const [min, max, price] of PRICING_TIERS) {
    if (userCount >= min && userCount < max) {
      return price
    }
  }
  // Fallback to max price if beyond all tiers
  return 40.0
}

/**
 * Check if payment is required for new signup
 * @param userCount Current number of active users
 * @returns true if payment is required (user count >= 250)
 */
export function isPaymentRequired(userCount: number): boolean {
  return userCount >= 250
}

/**
 * Get pricing tier information
 * @param userCount Current number of active users
 * @returns Object with price, tier info, and whether payment is required
 */
export function getPricingInfo(userCount: number) {
  const price = calculatePrice(userCount)
  const requiresPayment = isPaymentRequired(userCount)
  
  // Find current tier
  let currentTier = PRICING_TIERS.find(([min, max]) => userCount >= min && userCount < max)
  if (!currentTier) {
    // User count exceeds all tiers, use last tier
    currentTier = PRICING_TIERS[PRICING_TIERS.length - 1]
  }
  
  const [tierMin, tierMax, tierPrice] = currentTier
  
  return {
    price,
    priceInCents: Math.round(price * 100), // Stripe uses cents
    requiresPayment,
    currentTier: {
      min: tierMin,
      max: tierMax === Infinity ? null : tierMax,
      price: tierPrice,
    },
    userCount,
    nextTierPrice: userCount < 250 ? PRICING_TIERS[1][2] : (() => {
      // Find next tier price
      const nextTierIndex = PRICING_TIERS.findIndex(([min]) => userCount < min)
      if (nextTierIndex !== -1) {
        return PRICING_TIERS[nextTierIndex][2]
      }
      return null
    })(),
  }
}

/**
 * Check if test mode is enabled (bypass payment requirement)
 * 
 * NOTE: This is NOT the same as Stripe's test mode!
 * - STRIPE_TEST_MODE=true: Bypasses payment entirely (no Stripe checkout)
 * - Stripe Test Mode: Uses test API keys (sk_test_...) but still processes payments
 * 
 * To test the actual Stripe payment flow:
 * - Set STRIPE_TEST_MODE=false (payment required)
 * - Use Stripe test keys (sk_test_... and pk_test_...)
 * - Use Stripe test cards (4242 4242 4242 4242)
 * 
 * @returns true if STRIPE_TEST_MODE environment variable is set to 'true'
 */
export function isTestMode(): boolean {
  return process.env.STRIPE_TEST_MODE === 'true'
}

